import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import { OurIntlShape } from '@/i18n/typing'
import { designLayoutSize, PptxObjectType } from '@/utils/export-pptx/constants'
import { AnalysisSlide } from '@/utils/export-pptx/types/slide'

interface SlideProps {
  slide: AnalysisSlide
  onSlideReady?: (slide: AnalysisSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  return (
    <Wrapper data-pptx-object={PptxObjectType.Shape}>
      <TitleBlock>
        <Title data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'general:pptx_title_meta_analysis' })}
        </Title>
        <TopBrandLine data-pptx-object={PptxObjectType.Shape} />
      </TitleBlock>
      <BottomBrandLine data-pptx-object={PptxObjectType.Shape} />
      <Info>
        <Summary data-pptx-object={PptxObjectType.Text}>
          {slide.payload.summary}
        </Summary>
        <Content data-pptx-object={PptxObjectType.Shape}>
          <CardList>
            {slide.payload.cardList.map((card) => (
              <Card key={card.title}>
                <CardTitle data-pptx-object={PptxObjectType.Text}>
                  {card.title}
                </CardTitle>
                <CardValue data-pptx-object={PptxObjectType.Text}>
                  {card.value}
                </CardValue>
              </Card>
            ))}
          </CardList>
          <Currency data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:currency' })}
            {formatMessage({ id: 'general:colon' })}
            {slide.payload.currency}
          </Currency>
        </Content>
      </Info>
    </Wrapper>
  )
}

const BrandLine = styled.div`
  position: absolute;
  background: ${({ theme }): string => theme.colors.brand.primary};
  height: 1px;
`

const TopBrandLine = styled(BrandLine)`
  position: static;
  top: 72px;
  left: 358px;
`

const BottomBrandLine = styled(BrandLine)`
  bottom: 80px;
  left: 64px;
  right: 64px;
`

const Title = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 42px;
  font-weight: 700;
  line-height: 70px;
`

const TitleBlock = styled.div`
  position: absolute;
  top: 37px;
  left: 64px;
  right: 64px;
  display: flex;
  gap: 24px;

  ${Title} {
    flex: 0 0 auto;
  }

  ${TopBrandLine} {
    margin-top: 34px;
    flex: 1 1 auto;
  }
`

const Wrapper = styled.div`
  font-family: Arial;
  width: ${designLayoutSize.w}px;
  height: ${designLayoutSize.h}px;
  position: relative;
  /* border: solid 1px ${({ theme }): string => theme.colors.text.primary}; */
  color: ${({ theme }): string => theme.colors.text.primary};
  background-color: ${({ theme }): string => theme.colors.border.divider};

  &,
  * {
    box-sizing: border-box;
  }
`

const Summary = styled.div`
  font-size: 15px;
  line-height: 24px;
  text-align: center;
  width: 72%;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const Currency = styled.div`
  font-size: 14px;
  line-height: 19px;
  color: ${({ theme }): string => theme.colors.text.tooltip};
`

const CardTitle = styled.div`
  font-size: 20px;
  line-height: 28px;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const CardValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  line-height: 44.8px;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const Card = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  min-width: 150px;
`

const CardList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 40px;
`

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border-radius: 16px;
  gap: 48px;
  width: 1312px;
  padding: 38px 40px 40px;
  background: ${({ theme }): string => theme.colors.text.white};
`

const Info = styled.div`
  position: absolute;
  top: 120px;
  left: 24px;
  right: 24px;
  height: 572px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;

  ${Content} {
    flex: 1 1 auto;
  }
`

export default SlideComponent
